import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type StoreCreditStatus = "PENDIENTE" | "PAGADO" | "ANULADO";
export type StoreCreditInstallmentStatus = "PENDIENTE" | "PAGADA" | "ANULADA";

export const STORE_CREDIT_STATUS_LABEL: Record<StoreCreditStatus, string> = {
  PENDIENTE: "Pendiente",
  PAGADO: "Pagado",
  ANULADO: "Anulado",
};

export interface StoreCreditInstallmentRecord {
  id: string;
  number: number;
  dueDate: string;
  amount: string;
  paidAmount: string;
  status: StoreCreditInstallmentStatus;
}

export interface StoreCreditPaymentRecord {
  id: string;
  amount: string;
  paymentDate: string;
  notes: string | null;
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
}

export interface StoreCreditRecord {
  id: string;
  saleId: string;
  storeCustomerId: string;
  branchId: string;
  principal: string;
  balance: string;
  status: StoreCreditStatus;
  notes: string | null;
  isOverdue: boolean;
  createdAt: string;
  sale?: { id: string; saleNumber: number; saleDate: string; total: string };
  storeCustomer?: { id: string; name: string; documentId: string | null; phone: string | null };
  branch?: { id: string; name: string };
  installments: StoreCreditInstallmentRecord[];
  payments: StoreCreditPaymentRecord[];
}

export interface ListStoreCreditsFilters {
  page?: number;
  pageSize?: number;
  storeCustomerId?: string;
  status?: StoreCreditStatus;
  overdueOnly?: boolean;
}

/** Ventas a crédito (fiado) del POS — cuotas y abonos. Ver store-credit.service.ts en el backend. */
export function useStoreCredits(filters: ListStoreCreditsFilters = {}) {
  return useQuery({
    queryKey: ["store", "credits", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StoreCreditRecord[]>>("/store-credits", { params: filters });
      return data;
    },
  });
}

export function useStoreCredit(id: string | null) {
  return useQuery({
    queryKey: ["store", "credits", "detail", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StoreCreditRecord>>(`/store-credits/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useRegisterCreditPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amount, paymentDate, notes }: { id: string; amount: number | string; paymentDate?: string; notes?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<StoreCreditRecord>>(`/store-credits/${id}/payments`, { amount, paymentDate, notes });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "credits"] }),
  });
}

export interface CustomerCreditSummaryRow {
  storeCustomerId: string;
  customerName: string;
  pendingCount: number;
  pendingBalance: number;
  overdueCount: number;
  overdueBalance: number;
}

/** Cobranza: créditos pendientes/vencidos agrupados por cliente. */
export function useCustomerCreditSummary() {
  return useQuery({
    queryKey: ["store", "credits", "customer-summary"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CustomerCreditSummaryRow[]>>("/store-credits/customer-summary");
      return data.data;
    },
  });
}
